// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useRef, useState } from 'react'
import { formatCell, formatUsDateLocal } from '../../api/format'
import { Plus, X, Play, Save, Lock, Code2, Download, Database, ChevronUp, ChevronDown, GripVertical } from 'lucide-react'
import { usePointerReorder, reorderByKey } from '../../components/usePointerReorder'
import * as api from '../../api/client'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { buildReportSpec, type SelFilter } from './reportSpec'
import ReportAiKeyCard from './ReportAiKeyCard'
import ReportAiChat from './ReportAiChat'
import {
  computeImpact, aiPropose, classOf, COMPUTED, TEMPLATES, PREVIEW_ROLES,
  type TemplateKey, type Impact,
} from './reportLogic'

const AGGS = ['none', 'count', 'sum', 'avg', 'min', 'max'] as const
const OPS = ['=', '<>', '>', '>=', '<', '<=', 'like'] as const

type ColView = { fieldId: number; table: string; column: string; label: string; sensitivity: string; agg: string }

/**
 * Report Builder (ADR-020). Faithful build of the approved mockup
 * (docs/design/report-builder-mockup.html): describe-it AI proposer with
 * heavy-query pushback, the Impact meter, templates, computed columns, group-by,
 * View SQL, CSV export, and preview-as-role — wired to the real governed engine.
 * Masking is enforced server-side; preview-as re-runs server-side per role.
 */
export default function ReportBuilderPage() {
  const [pharmacies, setPharmacies] = useState<api.PharmacyListItem[]>([])
  const [pharmacyId, setPharmacyId] = useState('')
  const [fieldQuery, setFieldQuery] = useState('')
  const [catalog, setCatalog] = useState<api.ViewFieldCatalogItem[]>([])
  const [columns, setColumns] = useState<ColView[]>([])
  const [computed, setComputed] = useState<string[]>([])
  const [filters, setFilters] = useState<SelFilter[]>([])
  const [result, setResult] = useState<api.ReportRunResult | null>(null)
  // D-UI-2: a funnel on every result column. It filters the whole result, and paging happens after.
  const resultFunnels = useMemo<FunnelColumn<Record<string, any>>[]>(() => {
    const masked = new Set(result?.masked_columns ?? [])
    return (result?.columns ?? []).map(c => ({
      key: c, label: c,
      value: (row: Record<string, any>) => masked.has(c) ? 'restricted' : funnelCell(c, row[c]),
    }))
  }, [result])
  const rfx = useTableFunnels(result?.rows, resultFunnels)
  const rResetKey = useMemo(() => [result, rfx.sig], [result, rfx.sig])   // a new run OR a funnel change returns to page 1
  const { page: rPage, setPage: setRPage, pageSize: rPageSize, setPageSize: setRPageSize, pageRows: rPageRows, total: rTotal, totalPages: rTotalPages } = usePaged(rfx.rows, DEFAULT_PAGE_SIZE, rResetKey)
  const [sql, setSql] = useState<string | null>(null)
  const [showSql, setShowSql] = useState(false)
  const [previewRole, setPreviewRole] = useState(PREVIEW_ROLES[0])
  const [proposal, setProposal] = useState<{ key: TemplateKey; heavy: boolean } | null>(null)
  const [reportName, setReportName] = useState('Untitled report')
  const [bgLog, setBgLog] = useState<string[]>([])
  const [saved, setSaved] = useState<api.SavedReportSummary[]>([])
  const [loadedId, setLoadedId] = useState<string | null>(null)
  const [loadedPurpose, setLoadedPurpose] = useState('operations')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280)
  const resultsRef = useRef<HTMLDivElement | null>(null)
  const narrow = vw < 1024

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    api.getViewCatalog().then(r => setCatalog(r.items)).catch(e => setError(String(e?.message ?? e)))
    api.listPharmacies().then(r => {
      setPharmacies(r.items)
      if (r.items.length) setPharmacyId(p => p || r.items[0].id)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!pharmacyId) return
    api.listReports(pharmacyId).then(r => setSaved(r.reports)).catch(() => setSaved([]))
  }, [pharmacyId])

  const findField = (table: string, column: string) =>
    catalog.find(c => c.source_table === table && c.column_name === column)
  const grouped = useMemo(() => {
    const g = new Map<string, api.ViewFieldCatalogItem[]>()
    catalog.forEach(c => { const k = c.category || c.source_table || 'Other'; (g.get(k) ?? g.set(k, []).get(k)!).push(c) })
    return Array.from(g.entries())
  }, [catalog])

  const visibleGroups = useMemo(() => {
    const q = fieldQuery.trim().toLowerCase()
    if (!q) return grouped
    return grouped
      .map(([cat, items]) => [cat, items.filter(it =>
        it.display_label.toLowerCase().includes(q) ||
        (it.column_name || '').toLowerCase().includes(q) ||
        (cat || '').toLowerCase().includes(q))] as [string, api.ViewFieldCatalogItem[]])
      .filter(([, items]) => items.length > 0)
  }, [grouped, fieldQuery])

  // ---- impact (the signature) ----
  const impact: Impact = useMemo(() => {
    const tables = new Set(columns.map(c => c.table))
    if (computed.length) tables.add('rx_claims')
    return computeImpact({
      tables: [...tables],
      hasAgg: columns.some(c => c.agg !== 'none') || computed.length > 0,
      anyPhi: columns.some(c => c.sensitivity === 'phi' || c.sensitivity === 'phi_high'),
      filterCount: filters.filter(f => f.value.trim() !== '').length,
      columnCount: columns.length + computed.length,
    })
  }, [columns, computed, filters])

  const addColumn = (it: api.ViewFieldCatalogItem) => {
    if (columns.some(c => c.fieldId === it.id)) return
    setColumns([...columns, { fieldId: it.id, table: it.source_table, column: it.column_name, label: it.display_label, sensitivity: it.sensitivity, agg: 'none' }])
  }
  const toggleComputed = (key: string) =>
    setComputed(computed.includes(key) ? computed.filter(k => k !== key) : [...computed, key])

  const groupByCols = columns.filter(c => c.agg === 'none')
  const hasAgg = columns.some(c => c.agg !== 'none') || computed.length > 0

  const loadTemplate = (key: TemplateKey) => {
    if (!catalog.length) { setError('Field catalog is still loading — try again in a moment.'); return }
    const t = TEMPLATES[key]
    const cols: ColView[] = []
    t.cols.forEach(([tbl, col]) => {
      const it = findField(tbl, col)
      if (it) cols.push({ fieldId: it.id, table: it.source_table, column: it.column_name, label: it.display_label, sensitivity: it.sensitivity, agg: t.aggs[col] ?? 'none' })
    })
    if (cols.length === 0) { setError(`Couldn't map "${t.name}" to this pharmacy's fields.`); return }
    setError(null)
    setColumns(cols)
    setComputed(t.computed.filter(k => k in COMPUTED))
    const f: SelFilter[] = []
    t.filters.forEach(([col, op, val]) => { const it = catalog.find(c => c.column_name === col); if (it) f.push({ fieldId: it.id, op, value: val }) })
    setFilters(f)
  }

  const ask = (text: string) => {
    const t = text.trim(); if (!t) return
    const p = aiPropose(t)
    loadTemplate(p.key)
    setReportName(t.slice(0, 64))
    setProposal(p)
  }

  // Apply a validated spec from the chat assistant (field ids → builder state).
  const applyAiSpec = (spec: api.ReportAiSpec) => {
    const cols: ColView[] = []
    spec.columns.forEach(sc => {
      const it = catalog.find(c => c.id === sc.fieldId)
      if (it) cols.push({ fieldId: it.id, table: it.source_table, column: it.column_name, label: it.display_label, sensitivity: it.sensitivity, agg: sc.agg })
    })
    if (cols.length) setColumns(cols)
    setComputed((spec.computed || []).filter(k => k in COMPUTED))
    setFilters((spec.filters || []).map(f => ({ fieldId: f.fieldId, op: f.op, value: f.value })))
    setProposal(null)
  }
  // Load a saved report into the builder for editing (tolerant of spec JSON casing).
  // Map a saved spec's JSON (tolerant of casing) into builder state shapes.
  const mapSavedSpec = (sp: any) => {
    const cols: ColView[] = []
    ;(sp.columns ?? sp.Columns ?? []).forEach((sc: any) => {
      const fid = sc.fieldId ?? sc.FieldId
      const it = catalog.find(c => c.id === fid)
      if (it) cols.push({ fieldId: it.id, table: it.source_table, column: it.column_name, label: it.display_label, sensitivity: it.sensitivity, agg: (sc.agg ?? sc.Agg ?? 'none') })
    })
    const comp = (sp.computed ?? sp.Computed ?? []).filter((k: string) => k in COMPUTED)
    const flt = (sp.filters ?? sp.Filters ?? []).map((f: any) => ({ fieldId: f.fieldId ?? f.FieldId, op: f.op ?? f.Op ?? '=', value: f.value ?? f.Value ?? '' }))
    return { cols, comp, flt }
  }

  const loadSaved = async (id: string) => {
    setBusy(true); setError(null)
    try {
      const raw = await api.getSavedReport(id)
      const { cols, comp, flt } = mapSavedSpec(raw.spec || {})
      setColumns(cols); setComputed(comp); setFilters(flt)
      setReportName(raw.name); setLoadedPurpose(raw.purpose_of_use || 'operations')
      setLoadedId(id); setProposal(null)
      window.scrollTo({ top: 0, behavior: 'smooth' })   // builder is at the top; bring it into view
    } catch (e: any) { setError(fmtErr(e, 'Load failed')) } finally { setBusy(false) }
  }

  // Load a saved report into the builder AND run it (results attributed to the report in the audit).
  const runSaved = async (id: string) => {
    setBusy(true); setError(null); setProposal(null)
    try {
      const raw = await api.getSavedReport(id)
      const { cols, comp, flt } = mapSavedSpec(raw.spec || {})
      if (!cols.length) { setError('That report has no fields that exist in the current catalog.'); return }
      setColumns(cols); setComputed(comp); setFilters(flt)
      setReportName(raw.name); setLoadedPurpose(raw.purpose_of_use || 'operations'); setLoadedId(id)
      const gb = (cols.some(c => c.agg !== 'none') || comp.length) ? cols.filter(c => c.agg === 'none').map(c => c.fieldId) : []
      const r = await api.runReport({ ...buildReportSpec({ pharmacyId, columns: cols, filters: flt, computed: comp, groupBy: gb, previewRole }), sourceReportId: id })
      setResult(r); setSql(r.sql ?? null)
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    } catch (e: any) { setError(fmtErr(e, 'Run failed')); setResult(null) } finally { setBusy(false) }
  }

  // Overwrite the loaded report in place.
  const update = async () => {
    if (!loadedId) return
    setBusy(true); setError(null)
    try {
      await api.updateReport(loadedId, { ...spec(), name: reportName, purposeOfUse: loadedPurpose })
      if (pharmacyId) setSaved((await api.listReports(pharmacyId)).reports)
    } catch (e: any) { setError(fmtErr(e, 'Update failed')) } finally { setBusy(false) }
  }

  const removeSaved = async (id: string, name: string) => {
    if (!window.confirm(`Delete saved report "${name}"? This cannot be undone.`)) return
    setBusy(true); setError(null)
    try {
      await api.deleteReport(id)
      if (pharmacyId) setSaved((await api.listReports(pharmacyId)).reports)
      if (loadedId === id) setLoadedId(null)
    } catch (e: any) { setError(fmtErr(e, 'Delete failed')) } finally { setBusy(false) }
  }

  const newReport = () => { setLoadedId(null); setReportName('Untitled report'); setColumns([]); setComputed([]); setFilters([]); setProposal(null) }

  // Reorder a selected column (column order = output column order).
  const moveColumn = (i: number, dir: -1 | 1) => setColumns(cols => {
    const j = i + dir
    if (j < 0 || j >= cols.length) return cols
    const next = [...cols]
    ;[next[i], next[j]] = [next[j], next[i]]
    return next
  })
  // Drag-to-reorder report columns (ASP ColumnManager pattern; arrows kept for a11y).
  const colDrag = usePointerReorder((from, to) => setColumns(cols => reorderByKey(cols, (c: any) => String(c.fieldId), from, to)))

  const applySimplified = () => {
    const wf = findField('rx_items', 'written_at')
    if (wf && !filters.some(f => f.fieldId === wf.id)) setFilters(f => [...f, { fieldId: wf.id, op: '>', value: '2026-03-18' }])
    setProposal(p => p ? { ...p, heavy: false } : p)
  }

  const spec = (): api.ReportRunSpec => buildReportSpec({
    pharmacyId, columns, filters, computed,
    groupBy: groupByCols.map(c => c.fieldId), previewRole,
  })

  const run = async (breakGlass = false) => {
    if (!columns.length) { setError('Add at least one column (use the field list or a template) before running.'); return }
    setBusy(true); setError(null); setProposal(null)
    try {
      const r = await api.runReport({ ...spec(), breakGlass })
      setResult(r); setSql(r.sql ?? null)
      if (breakGlass) setBgLog(l => [`${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · BREAK-GLASS heavy run`, ...l])
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    } catch (e: any) { setError(fmtErr(e, 'Run failed')); setResult(null) }
    finally { setBusy(false) }
  }
  const viewSql = async () => {
    if (!columns.length) { setError('Add a column or pick a template first, then View SQL.'); return }
    setBusy(true); setError(null)
    try { const r = await api.previewReportSql(spec()); setSql(r.sql); setShowSql(true) }
    catch (e: any) { setError(fmtErr(e, 'Could not build SQL')) }
    finally { setBusy(false) }
  }
  const onRole = async (role: string) => {
    setPreviewRole(role)
    if (!result || !columns.length) return
    setBusy(true)
    try {
      const r = await api.runReport(buildReportSpec({ pharmacyId, columns, filters, computed, groupBy: groupByCols.map(c => c.fieldId), previewRole: role }))
      setResult(r)
    } catch { /* keep prior */ } finally { setBusy(false) }
  }
  const exportCsv = () => {
    if (!result) return
    const esc = (v: any) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
    const rows = [result.columns.map(esc).join(','), ...result.rows.map(r => result.columns.map(c => esc(r[c])).join(','))]
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
    a.download = `${reportName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`; a.click()
    URL.revokeObjectURL(a.href)
  }
  const save = async () => {
    const name = window.prompt('Report name?', reportName); if (!name) return
    const purpose = window.prompt('Purpose of use (recorded for audit)?') || 'operations'
    setBusy(true); setError(null)
    try { await api.saveReport({ ...spec(), name, purposeOfUse: purpose }); if (pharmacyId) setSaved((await api.listReports(pharmacyId)).reports) }
    catch (e: any) { setError(fmtErr(e, 'Save failed')) } finally { setBusy(false) }
  }

  const lampGlyph = impact.level === 'green' ? '✓' : impact.level === 'amber' ? '!' : '‼'
  const verdict = !columns.length ? ['—', 'Build a report to see impact']
    : impact.level === 'green' ? ['Light', 'Safe to run anytime']
    : impact.level === 'amber' ? ['Moderate', 'Fine — runs on the reporting replica']
    : ['Heavy', 'This could be slow — narrow it down']
  const sensColor = (s: string) => classOf('', s) === 'phi' ? '#8a5cd0' : '#8a98a6'

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Report builder</h2>
        <span style={{ fontSize: 11, color: '#8a98a6' }}>SaaS-admin · governed, read-only, runs on the replica</span>
        <select value={pharmacyId} onChange={e => { setPharmacyId(e.target.value); setResult(null) }} style={{ ...select, marginLeft: 'auto', maxWidth: '100%' }}>
          {pharmacies.length === 0 && <option value="">(your pharmacy)</option>}
          {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {error && (
        <div role="alert" style={{ background: '#fbe9e9', color: '#a23333', border: '1px solid #f0c4c4', borderRadius: 8, padding: '10px 13px', fontSize: 13, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ fontWeight: 700 }}>⚠</span><span>{error}</span>
          <button onClick={() => setError(null)} style={{ ...xBtn, marginLeft: 'auto' }} aria-label="Dismiss"><X size={15} /></button>
        </div>
      )}
      {!catalog.length && (
        <div style={{ background: '#fff6e6', color: '#8a6111', border: '1px solid #f0d28a', borderRadius: 8, padding: '10px 13px', fontSize: 13, marginBottom: 12 }}>
          No fields loaded yet. If this persists, the field catalog didn't load for your session — reload the page; if it's still empty, tell me and I'll check the catalog endpoint for your role.
        </div>
      )}

      <ReportAiKeyCard />

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '240px minmax(0, 1fr) 300px', gap: 16, alignItems: 'start' }}>
        {/* CATALOG */}
        <aside style={{ ...card, display: 'flex', flexDirection: 'column', ...(narrow ? { maxHeight: 340 } : { maxHeight: 'calc(100vh - 150px)' }) }}>
          <h3 style={{ ...h3, marginBottom: 8 }}>Fields</h3>
          <input
            value={fieldQuery}
            onChange={e => setFieldQuery(e.target.value)}
            placeholder="Search fields…"
            aria-label="Search fields"
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e4e9ef', borderRadius: 7, padding: '7px 10px', fontSize: 13, marginBottom: 10 }}
          />
          <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, marginRight: -6, paddingRight: 6 }}>
            {visibleGroups.length === 0 && <p style={empty}>No fields match “{fieldQuery}”.</p>}
            {visibleGroups.map(([cat, items]) => (
              <div key={cat} style={{ marginBottom: 10 }}>
                <div style={catHdr}>{cat}</div>
                {items.map(it => (
                  <div key={it.id} style={fieldRow}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.display_label}</span>
                    {it.sensitivity !== 'low' && <span style={{ fontSize: 9, fontWeight: 700, color: sensColor(it.sensitivity), textTransform: 'uppercase' }}>{it.sensitivity}</span>}
                    <button aria-label={`Add ${it.display_label}`} onClick={() => addColumn(it)} style={addBtn}><Plus size={13} /></button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* BUILDER */}
        <main>
          {/* chat assistant (falls back to keyword router when AI key not set) */}
          <ReportAiChat pharmacyId={pharmacyId} onApplySpec={applyAiSpec} onKeywordFallback={ask} />
          {proposal && (
            <div style={{ ...card, ...(proposal.heavy ? aicardHeavy : aicardOk) }}>
              {proposal.heavy ? (
                <>
                  <div style={aihead}><span style={aibadge}>AI</span> I can build that — but it's heavy. Pushing back.</div>
                  <div style={{ fontSize: 12.5, margin: '7px 0' }}>Your request pulls <b>every patient across all history with no date window</b>. Estimated <b>~480,000 rows</b>, plan cost <b>~14,200</b> — red on the impact meter, and it would tie up the reporting replica.</div>
                  <div style={{ fontSize: 12.5, margin: '7px 0' }}><b>Lighter version I'd run instead:</b> same columns, <b>last 90 days</b>, grouped by drug &amp; payer. Much lighter — green.</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={applySimplified} style={primaryBtn}>Use the lighter version</button>
                    <button onClick={() => run(true)} style={dangerBtn}>Run heavy anyway (break-glass)</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={aihead}><span style={aibadge}>AI</span> Here's what I'll run.</div>
                  <div style={{ fontSize: 12.5, margin: '7px 0' }}>{columns.map(c => (c.agg !== 'none' ? c.agg + ' of ' : '') + c.label).concat(computed.map(k => COMPUTED[k].label)).join(', ') || '(nothing yet)'}</div>
                  <div style={{ fontSize: 12, color: '#5d6b7b' }}>Impact <b style={{ color: `var(--${impact.level})`, textTransform: 'uppercase' }}>{impact.level}</b> · ~{impact.rowsScanned.toLocaleString()} rows · runs under your session on the replica.</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => run()} style={primaryBtn}>Run</button>
                    <button onClick={() => setProposal(null)} style={ghost}>Adjust in builder</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* columns */}
          <div style={card}>
            <h3 style={h3}>Columns {columns.length ? `(${columns.length})` : ''}</h3>
            {columns.length === 0 && <p style={empty}>Add fields from the left, or describe the report above.</p>}
            {columns.map((c, i) => (
              <div key={c.fieldId} {...colDrag.rowProps(String(c.fieldId))} style={{ ...selRow, outline: colDrag.isDragging(String(c.fieldId)) ? '1.5px solid var(--color-accent, #0f766e)' : undefined }}>
                <span {...colDrag.handleProps(String(c.fieldId))}><GripVertical size={12} style={{ color: 'var(--color-text-mute)' }} /></span>
                <div style={{ display: 'flex', flexDirection: 'column', marginRight: 2 }}>
                  <button aria-label={`Move ${c.label} up`} onClick={() => moveColumn(i, -1)} disabled={i === 0} style={{ ...moveBtn, opacity: i === 0 ? 0.3 : 1, cursor: i === 0 ? 'default' : 'pointer' }}><ChevronUp size={12} /></button>
                  <button aria-label={`Move ${c.label} down`} onClick={() => moveColumn(i, 1)} disabled={i === columns.length - 1} style={{ ...moveBtn, opacity: i === columns.length - 1 ? 0.3 : 1, cursor: i === columns.length - 1 ? 'default' : 'pointer' }}><ChevronDown size={12} /></button>
                </div>
                <span style={{ flex: 1, fontWeight: 600 }}>{c.label}{c.sensitivity !== 'low' && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: sensColor(c.sensitivity), textTransform: 'uppercase' }}>{c.sensitivity}</span>}</span>
                <select value={c.agg} onChange={e => setColumns(columns.map((x, j) => j === i ? { ...x, agg: e.target.value } : x))} style={miniSelect}>
                  {AGGS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <button aria-label="Remove column" onClick={() => setColumns(columns.filter((_, j) => j !== i))} style={xBtn}><X size={14} /></button>
              </div>
            ))}
          </div>

          {/* group-by (auto-derived when aggregating) */}
          {hasAgg && groupByCols.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Grouped by</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {groupByCols.map(c => <span key={c.fieldId} style={gchip}>{c.label}</span>)}
              </div>
            </div>
          )}

          {/* computed columns */}
          <div style={card}>
            <h3 style={h3}>Computed columns</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {Object.values(COMPUTED).map(c => {
                const on = computed.includes(c.key)
                return <button key={c.key} onClick={() => toggleComputed(c.key)} style={{ ...cpreset, ...(on ? cpresetOn : {}) }}>{on ? '✓ ' : '＋ '}{c.label}</button>
              })}
            </div>
          </div>

          {/* filters */}
          <div style={card}>
            <h3 style={h3}>Filters</h3>
            {filters.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select value={f.fieldId} onChange={e => setFilters(filters.map((x, j) => j === i ? { ...x, fieldId: Number(e.target.value) } : x))} style={{ ...miniSelect, flex: 1.3 }}>
                  {catalog.map(c => <option key={c.id} value={c.id}>{c.display_label}</option>)}
                </select>
                <select value={f.op} onChange={e => setFilters(filters.map((x, j) => j === i ? { ...x, op: e.target.value } : x))} style={miniSelect}>
                  {OPS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <input value={f.value} placeholder="value" onChange={e => setFilters(filters.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} style={{ ...input, flex: 1 }} />
                <button aria-label="Remove filter" onClick={() => setFilters(filters.filter((_, j) => j !== i))} style={xBtn}><X size={14} /></button>
              </div>
            ))}
            <button onClick={() => catalog[0] && setFilters([...filters, { fieldId: catalog[0].id, op: '=', value: '' }])} style={ghost}>+ Add filter</button>
          </div>

          {loadedId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#eef6ff', border: '1px solid #cfe2f7', borderRadius: 7, padding: '7px 11px', marginBottom: 10, fontSize: 12.5 }}>
              <span>Editing saved report: <b>{reportName}</b></span>
              <button onClick={newReport} style={{ ...ghost, marginLeft: 'auto', padding: '4px 10px' }}>New report</button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => run()} disabled={busy || !columns.length} style={{ ...primaryBtn, opacity: (busy || !columns.length) ? 0.5 : 1 }}><Play size={14} /> {busy ? 'Running…' : 'Run report'}</button>
            {loadedId ? (
              <>
                <button onClick={update} disabled={busy || !columns.length} style={{ ...ghost, opacity: (busy || !columns.length) ? 0.5 : 1 }}><Save size={14} /> Update report</button>
                <button onClick={save} disabled={busy || !columns.length} style={{ ...ghost, opacity: (busy || !columns.length) ? 0.5 : 1 }}>Save as new</button>
              </>
            ) : (
              <button onClick={save} disabled={busy || !columns.length} style={{ ...ghost, opacity: (busy || !columns.length) ? 0.5 : 1 }}><Save size={14} /> Save to pharmacy reports</button>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#8a98a6', marginTop: 7 }}>
            {!columns.length ? 'Add at least one field (or pick a template) to enable Run.' : 'Runs under your session · read-only on the replica'}
          </div>
        </main>

        {/* IMPACT */}
        <aside style={{ ...card, padding: 0 }}>
          <div style={{ padding: '13px 15px', borderBottom: '1px solid #e4e9ef' }}><h3 style={{ ...h3, margin: 0 }}>Impact</h3></div>
          <div style={{ padding: 15 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 11 }}>
              <div style={{ ...lamp, background: `var(--${impact.level})` }}>{columns.length ? lampGlyph : '·'}</div>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>{verdict[0]}</div><div style={{ fontSize: 11.5, color: '#5d6b7b' }}>{verdict[1]}</div></div>
            </div>
            <div style={{ height: 7, background: '#eef2f6', borderRadius: 99, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ height: '100%', width: (columns.length ? impact.pct : 6) + '%', background: `var(--${impact.level})`, transition: 'width .25s' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 12 }}>
              <Stat label="Rows scanned" value={'~' + (columns.length ? impact.rowsScanned.toLocaleString() : '0')} />
              <Stat label="Tables" value={String(impact.nTables)} />
              <Stat label="Est. time" value={'~' + (impact.ms / 1000).toFixed(impact.ms < 1000 ? 2 : 1) + 's'} />
              <Stat label="Plan cost" value={columns.length ? impact.cost.toLocaleString() : '0'} />
            </div>
            <div style={{ fontSize: 12, color: '#5d6b7b', lineHeight: 1.5 }}>
              {columns.length ? <>This report <b>{impact.drivers.join(', ')}</b>. It runs against the read-only reporting replica, so it can't slow down the pharmacies' live system.</> : 'Add some columns to see the impact.'}
            </div>
            {impact.level !== 'green' && columns.length > 0 && (
              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 7, fontSize: 12, background: impact.level === 'red' ? '#fbe9e9' : '#fff6e6', color: impact.level === 'red' ? '#a23333' : '#8a6111' }}>
                {impact.level === 'red' ? '⚠ Heavy report. Add a date range or a payer filter to bring this down before running.' : 'Tip: a date filter would make this noticeably faster.'}
              </div>
            )}
            <label style={{ display: 'block', marginTop: 15, fontSize: 11, fontWeight: 700, color: '#5d6b7b' }}>Preview as
              <select value={previewRole} onChange={e => onRole(e.target.value)} style={{ ...select, display: 'block', marginTop: 5, width: '100%', minWidth: 0 }}>
                {PREVIEW_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={viewSql} disabled={!columns.length} style={{ ...ghost, flex: 1, justifyContent: 'center' }}><Code2 size={13} /> View SQL</button>
              <button onClick={exportCsv} disabled={!result} style={{ ...ghost, flex: 1, justifyContent: 'center' }}><Download size={13} /> CSV</button>
            </div>
            {bgLog.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 11 }}>
                <div style={{ ...h3, margin: '0 0 5px' }}>Break-glass log</div>
                {bgLog.map((l, i) => <div key={i} style={{ color: '#a23333', padding: '2px 0' }}>⚠ {l}</div>)}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* SQL panel */}
      {showSql && sql && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Code2 size={14} /><span style={{ fontWeight: 700, fontSize: 13 }}>Generated SQL</span>
            <span style={{ fontSize: 11, color: '#8a98a6' }}>read-only · tenant-scoped · runs on replica</span>
            <button onClick={() => setShowSql(false)} style={{ ...xBtn, marginLeft: 'auto' }}><X size={15} /></button>
          </div>
          <pre style={{ background: '#0f1b2a', color: '#cfe3f2', padding: 14, borderRadius: 8, fontSize: 12, overflow: 'auto', margin: 0 }}>{sql}</pre>
        </div>
      )}

      {/* results */}
      {result && (
        <div ref={resultsRef} style={{ ...card, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Database size={14} />
            <span style={{ fontSize: 12, color: '#5d6b7b' }}>{result.count} rows · {result.runtime_ms}ms · {pharmacies.find(p => p.id === pharmacyId)?.name ?? 'your pharmacy'}{result.preview_role ? ` · viewing as ${result.preview_role}` : ''}</span>
            {result.masked_columns.length > 0 && <span style={{ fontSize: 11, color: '#cf4747', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Lock size={12} /> {result.masked_columns.length} column(s) masked</span>}
          </div>
          {result.count === 0 && (
            <div style={{ fontSize: 12.5, color: '#8a6111', background: '#fff6e6', borderRadius: 7, padding: '9px 11px', marginBottom: 8 }}>
              No rows for this pharmacy. The synthetic data lives under one tenant — switch the pharmacy at the top right, or loosen any filters.
            </div>
          )}
          {/* POST /reports/run hard-caps at 5,000 rows server-side — disclose when a result lands on the cap. */}
          {result.rows.length === 5000 && (
            <div role="status" style={{ border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f', fontSize: 12.5, borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
              This report hit the 5,000-row ceiling — rows beyond it are not shown. Narrow the report definition.
            </div>
          )}
          <Pager page={rPage} totalPages={rTotalPages} total={rTotal} pageSize={rPageSize} onPage={setRPage} onPageSize={setRPageSize} label="rows" />
          {rfx.chips}
          <div style={{ overflow: "auto", maxHeight: 380 }}>
            <table {...rfx.tableProps} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
              <thead><tr>{result.columns.map(c => <th key={c} style={th}>{result.masked_columns.includes(c) ? <><Lock size={11} /> {c}</> : c} {rfx.funnel(c)}</th>)}</tr></thead>
              <tbody>
                {rPageRows.map((row, ri) => (
                  <tr key={ri}>{result.columns.map(c => {
                    const masked = result.masked_columns.includes(c); const v = row[c]
                    return <td key={c} style={masked ? tdMasked : td}>{masked ? '🔒 restricted' : formatCell(c, v)}</td>
                  })}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={rPage} totalPages={rTotalPages} total={rTotal} pageSize={rPageSize} onPage={setRPage} showPageSize={false} label="rows" />
        </div>
      )}

      {saved.length > 0 && (
        <div style={{ ...card, marginTop: 16 }}>
          <h3 style={h3}>Saved reports for this pharmacy</h3>
          {saved.map(s => (
            <div key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eef2f6', fontSize: 12.5 }}>
              <span style={{ flex: 1 }}>{s.name}</span>
              <span style={{ color: '#8a98a6', fontSize: 11 }}>{formatUsDateLocal(s.created_at)}</span>
              <button onClick={() => runSaved(s.id)} disabled={busy} style={{ ...primaryBtn, padding: '3px 12px', fontSize: 12 }}><Play size={12} /> Run</button>
              <button onClick={() => loadSaved(s.id)} disabled={busy} style={{ ...ghost, padding: '3px 10px', fontSize: 12 }}>Edit</button>
              <button onClick={() => removeSaved(s.id, s.name)} disabled={busy} style={{ background: '#fff', border: '1px solid #f0c9c9', color: '#b3261e', padding: '3px 10px', borderRadius: 7, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// What a result cell shows, as a funnel value: a date raw (a calendar date as its bare day, so it
// is compared as written, the way formatCell prints it), a number raw, anything else as formatted.
function funnelCell(col: string, v: unknown): unknown {
  if (v == null || v === '') return null
  if (typeof v === 'number') return v
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2}))?/)
  if (m) return !m[2] || (m[2] === '00' && m[3] === '00') ? m[1] : v
  return formatCell(col, v)
}

function fmtErr(e: any, fallback: string): string {
  const status = e?.status ?? e?.response?.status
  const serverMsg = e?.body?.error ?? e?.body?.message ?? e?.message
  const human: Record<string, string> = {
    columns_required: 'No columns resolved — add a field or pick a template.',
    pharmacy_required: 'Pick a pharmacy first.',
    query_failed: 'The query failed to run (often a non-numeric value in a financial column). Try removing the financial aggregate or computed column.',
    unknown_field: 'A selected field is no longer in the catalog — remove and re-add it.',
  }
  const msg0 = (serverMsg && human[serverMsg]) || serverMsg || fallback
  // Surface the real SQL detail for query failures so it's diagnosable, not a guess.
  const detail = e?.body?.message
  const msg = (serverMsg === 'query_failed' && detail && detail !== 'query_failed')
    ? `${msg0} — ${String(detail).slice(0, 220)}` : msg0
  if (status === 401 || status === 403) return `Not authorized for this action (${status}). Your session may have expired — reload and sign in again.`
  return status ? `${msg} (HTTP ${status})` : msg
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div style={{ background: '#fbfcfd', border: '1px solid #eef2f6', borderRadius: 7, padding: '7px 9px' }}>
    <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.4px', color: '#8a98a6', fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{value}</div>
  </div>
}

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e4e9ef', borderRadius: 9, padding: '13px 15px', marginBottom: 14 }
const h3: React.CSSProperties = { margin: '0 0 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.6px', color: '#8a98a6', fontWeight: 700 }
const catHdr: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#5d6b7b', margin: '8px 0 4px' }
const fieldRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', fontSize: 12.5 }
const selRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: '1px solid #eef2f6' }
const addBtn: React.CSSProperties = { border: '1px solid #e4e9ef', background: '#fff', color: '#0a7681', width: 22, height: 22, borderRadius: 5, display: 'grid', placeItems: 'center', cursor: 'pointer' }
const xBtn: React.CSSProperties = { border: 0, background: 'none', color: '#8a98a6', cursor: 'pointer', display: 'grid', placeItems: 'center' }
const moveBtn: React.CSSProperties = { border: 0, background: 'none', color: '#8a98a6', display: 'grid', placeItems: 'center', padding: 0, lineHeight: 0, height: 13 }
const select: React.CSSProperties = { border: '1px solid #e4e9ef', borderRadius: 7, padding: '7px 10px', fontSize: 13, fontWeight: 600, minWidth: 200 }
const miniSelect: React.CSSProperties = { border: '1px solid #e4e9ef', borderRadius: 6, padding: '4px 7px', fontSize: 12 }
const input: React.CSSProperties = { border: '1px solid #e4e9ef', borderRadius: 7, padding: '8px 10px', fontSize: 13 }
const primaryBtn: React.CSSProperties = { background: '#0c8f9c', color: '#fff', border: 0, padding: '9px 15px', borderRadius: 7, fontWeight: 600, fontSize: 13, display: 'inline-flex', gap: 7, alignItems: 'center', cursor: 'pointer' }
const dangerBtn: React.CSSProperties = { ...primaryBtn, background: '#c0392b' }
const ghost: React.CSSProperties = { background: '#fff', border: '1px solid #e4e9ef', color: '#16212e', padding: '9px 13px', borderRadius: 7, fontWeight: 600, fontSize: 13, display: 'inline-flex', gap: 7, alignItems: 'center', cursor: 'pointer' }
const cpreset: React.CSSProperties = { border: '1px solid #e4e9ef', background: '#fff', color: '#16212e', fontSize: 12, padding: '5px 11px', borderRadius: 99, fontWeight: 600, cursor: 'pointer' }
const cpresetOn: React.CSSProperties = { background: '#0c8f9c', color: '#fff', borderColor: '#0c8f9c' }
const gchip: React.CSSProperties = { background: '#16212e', color: '#dce5ef', fontSize: 11.5, padding: '4px 10px', borderRadius: 99, fontWeight: 600 }
const empty: React.CSSProperties = { fontSize: 12.5, color: '#8a98a6', fontStyle: 'italic' }
const aicardOk: React.CSSProperties = { background: '#f0faf9', borderColor: '#bfe6e2' }
const aicardHeavy: React.CSSProperties = { background: '#fff6e6', borderColor: '#f0d28a' }
const aihead: React.CSSProperties = { fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }
const aibadge: React.CSSProperties = { background: '#0c8f9c', color: '#fff', fontSize: 9.5, fontWeight: 800, padding: '2px 6px', borderRadius: 5, letterSpacing: '.5px' }
const lamp: React.CSSProperties = { width: 38, height: 38, borderRadius: 10, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 18, fontWeight: 800 }
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.5px', color: '#5d6b7b', padding: '9px 12px', borderBottom: '1px solid #e4e9ef', whiteSpace: 'nowrap', background: '#fbfcfd', position: 'sticky', top: 0 }
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #eef2f6', whiteSpace: 'nowrap' }
const tdMasked: React.CSSProperties = { ...td, color: '#cf4747', background: '#fbe9e9', fontWeight: 600 }
